/**
 * Phase 54.25 #5 — Sales summary cards.
 *
 * Compact 4-card dashboard rendered ABOVE the SalesList filter row.
 * Reacts instantly to the currently-selected date range / branch /
 * employee / status. Hits the new /api/usm/sales/summary aggregation
 * endpoint (single MongoDB pass) so it's cheap enough to refresh on
 * every filter change.
 *
 * NOT a replacement for the Accounting module — strictly a quick
 * operational overview for cash closing and daily management.
 * Reuses the existing ERP design language: white card, thin border,
 * neutral text, gold accent for hero numbers.
 *
 * Phase 54.27 — Toplam Ciro card is enriched with a compact
 * payment-method breakdown (Nakit / POS / Havale / Diğer). Rows with
 * a zero amount are hidden. The breakdown reuses the same summary
 * response — no extra request. Layout and typography of all four
 * cards is preserved.
 */
import React, { useEffect, useState, useRef } from "react";
import usmApi from "@/lib/usmApi";
import {
  Wallet, Receipt, Gem, ShoppingBasket,
  Banknote, CreditCard, Landmark, CircleDollarSign,
} from "lucide-react";

const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });
const INT = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });

// Phase 54.27 — payment method → { label, icon }. Backend emits the raw
// enum ("cash" | "card" | "transfer" | "other"); we translate to Turkish
// labels for display. Unknown methods fall back to CircleDollarSign so
// future backend enum additions render gracefully.
const METHOD_META = {
  cash:     { label: "Nakit",  Icon: Banknote },
  card:     { label: "POS",    Icon: CreditCard },
  transfer: { label: "Havale", Icon: Landmark },
  other:    { label: "Diğer",  Icon: CircleDollarSign },
};

export default function SalesSummaryCards({ filters }) {
  const [data, setData] = useState({
    total_sales: 0, total_transactions: 0, total_products_sold: 0, avg_basket: 0,
    payments_by_method: [],
  });
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);

  useEffect(() => {
    // Cancel any in-flight summary fetch before firing a new one so
    // rapid filter changes don't race and paint stale numbers.
    if (abortRef.current) { try { abortRef.current.abort(); } catch { /* noop */ } }
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    const params = new URLSearchParams();
    if (filters?.q)         params.set("q",           filters.q);
    if (filters?.date_from) params.set("date_from",   filters.date_from);
    if (filters?.date_to)   params.set("date_to",     filters.date_to);
    if (filters?.empId)     params.set("employee_id", filters.empId);
    if (filters?.branchId)  params.set("branch_id",   filters.branchId);
    if (filters?.status)    params.set("status",      filters.status);
    usmApi.get(`/sales/summary?${params.toString()}`, { signal: ac.signal })
      .then((r) => { setData(r.data || {}); setLoading(false); })
      .catch((e) => {
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError") return;
        setLoading(false);
      });
    return () => { try { ac.abort(); } catch { /* noop */ } };
  }, [
    filters?.q, filters?.date_from, filters?.date_to,
    filters?.empId, filters?.branchId, filters?.status,
  ]);

  const paymentRows = (data.payments_by_method || []).filter(
    (p) => p && p.amount != null && Number(p.amount) > 0
  );

  const cards = [
    {
      key: "sales", icon: Wallet, label: "Toplam Ciro",
      value: TRY.format(data.total_sales || 0),
      testid: "sales-summary-total-sales",
      breakdown: paymentRows,
    },
    { key: "tx",       icon: Receipt,         label: "İşlem Sayısı",     value: INT.format(data.total_transactions || 0), testid: "sales-summary-total-tx" },
    { key: "products", icon: Gem,             label: "Satılan Ürün",     value: INT.format(data.total_products_sold || 0), testid: "sales-summary-total-products" },
    { key: "avg",      icon: ShoppingBasket,  label: "Ort. Sepet",       value: TRY.format(data.avg_basket || 0),        testid: "sales-summary-avg-basket" },
  ];

  return (
    <div data-testid="sales-summary-cards" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div
            key={c.key}
            data-testid={c.testid}
            className="bg-white border border-[#E7E5E4] px-4 py-3 flex items-start gap-3"
          >
            <div className="w-9 h-9 shrink-0 bg-[#FAFAF9] border border-[#F5F5F4] flex items-center justify-center mt-0.5">
              <Icon size={16} className="text-[#C8A96A]" strokeWidth={1.6} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#71717A] leading-tight truncate">
                {c.label}
              </div>
              <div className={
                "font-finance text-[18px] leading-tight mt-0.5 truncate tabular-nums " +
                (loading ? "text-[#A1A1AA]" : "text-[#0A0A0A]")
              }>
                {c.value}
              </div>
              {/* Phase 54.27 — payment breakdown under Toplam Ciro.
                  Compact list, hidden entirely when there are no
                  payments (fresh filter with zero matches). */}
              {c.breakdown && c.breakdown.length > 0 && (
                <ul
                  data-testid="sales-summary-payment-breakdown"
                  className="mt-1.5 space-y-0.5 text-[11px] tabular-nums"
                >
                  {c.breakdown.map((row) => {
                    const meta = METHOD_META[row.method] || { label: row.method || "—", Icon: CircleDollarSign };
                    const RowIcon = meta.Icon;
                    return (
                      <li
                        key={row.method}
                        data-testid={`sales-summary-payment-${row.method}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="inline-flex items-center gap-1.5 text-[#71717A] truncate">
                          <RowIcon size={11} strokeWidth={1.6} className="text-[#A1A1AA]" />
                          <span className="truncate">{meta.label}</span>
                        </span>
                        <span className="text-[#27272A] font-medium shrink-0">
                          {TRY.format(row.amount || 0)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
